// ไฟล์: frontend/src/pages/admin/AdminArticlesPage.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import "./AdminPages.css";
import AdminSidebar from "../../components/layout/AdminSidebar";

const AdminArticlesPage = ({ user }) => {
  const [articles, setArticles] = useState([]);
  const [pendingArticles, setPendingArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  // สำหรับ Modal แก้ไขบทความ
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleTitle, setArticleTitle] = useState("");
  const [articleContent, setArticleContent] = useState("");
  const [articleCategory, setArticleCategory] = useState("");
  const [articleThumbnail, setArticleThumbnail] = useState(null);

  // สำหรับ Modal ดูรายละเอียดบทความ
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingArticle, setViewingArticle] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // ดึงข้อมูลบทความที่อนุมัติแล้ว
        const articlesResponse = await axios.get("/articles");
        setArticles(articlesResponse.data.articles);

        // ดึงข้อมูลบทความที่รอการอนุมัติ
        const pendingResponse = await axios.get("/admin/articles/pending");
        setPendingArticles(pendingResponse.data.articles);

        // ดึงข้อมูลหมวดหมู่
        const categoriesResponse = await axios.get("/categories");
        setCategories(categoriesResponse.data.categories);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: "ไม่สามารถดึงข้อมูลได้",
          confirmButtonText: "ตกลง",
        });
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // เปิด Modal แก้ไขบทความ
  const handleEditClick = (article) => {
    setEditingArticle(article);
    setArticleTitle(article.title);
    setArticleContent(article.content);
    setArticleCategory(article.category_id);
    setArticleThumbnail(null);
    setShowEditModal(true);
  };

  // เปิด Modal ดูรายละเอียดบทความ
  const handleViewClick = (article) => {
    setViewingArticle(article);
    setShowViewModal(true);
  };

  // บันทึกการแก้ไขบทความ
  const handleSaveEdit = async (e) => {
    e.preventDefault();

    try {
      const formData = new FormData();
      formData.append("title", articleTitle);
      formData.append("content", articleContent);
      formData.append("category_id", articleCategory);

      if (articleThumbnail) {
        formData.append("thumbnail", articleThumbnail);
      }

      await axios.put(`/admin/articles/${editingArticle.id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // อัพเดทข้อมูลในหน้าจอ
      const updatedArticles = articles.map((article) => {
        if (article.id === editingArticle.id) {
          return {
            ...article,
            title: articleTitle,
            content: articleContent,
            category_id: articleCategory,
            category_name: categories.find(
              (cat) => cat.id === parseInt(articleCategory)
            )?.name,
          };
        }
        return article;
      });

      setArticles(updatedArticles);
      setShowEditModal(false);

      Swal.fire({
        icon: "success",
        title: "สำเร็จ",
        text: "แก้ไขบทความเรียบร้อยแล้ว",
        confirmButtonText: "ตกลง",
      });
    } catch (error) {
      console.error("Error updating article:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถแก้ไขบทความได้",
        confirmButtonText: "ตกลง",
      });
    }
  };

  // อนุมัติบทความ
  const handleApproveArticle = async (articleId) => {
    try {
      await Swal.fire({
        title: "ยืนยันการอนุมัติ",
        text: "คุณต้องการอนุมัติบทความนี้ใช่หรือไม่?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "อนุมัติ",
        cancelButtonText: "ยกเลิก",
      }).then(async (result) => {
        if (result.isConfirmed) {
          await axios.put(`/admin/articles/${articleId}/approve`);

          // อัพเดทข้อมูลในหน้าจอ
          const approvedArticle = pendingArticles.find(
            (article) => article.id === articleId
          );
          setArticles([approvedArticle, ...articles]);
          setPendingArticles(
            pendingArticles.filter((article) => article.id !== articleId)
          );

          Swal.fire({
            icon: "success",
            title: "สำเร็จ",
            text: "อนุมัติบทความเรียบร้อยแล้ว",
            confirmButtonText: "ตกลง",
          });
        }
      });
    } catch (error) {
      console.error("Error approving article:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถอนุมัติบทความได้",
        confirmButtonText: "ตกลง",
      });
    }
  };

  // ลบบทความ
  const handleDeleteArticle = async (articleId, isPending = false) => {
    try {
      await Swal.fire({
        title: "ยืนยันการลบ",
        text: "คุณต้องการลบบทความนี้ใช่หรือไม่? การกระทำนี้ไม่สามารถย้อนกลับได้",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "ลบ",
        cancelButtonText: "ยกเลิก",
        confirmButtonColor: "#d33",
      }).then(async (result) => {
        if (result.isConfirmed) {
          await axios.delete(`/admin/articles/${articleId}`);

          // อัพเดทข้อมูลในหน้าจอ
          if (isPending) {
            setPendingArticles(
              pendingArticles.filter((article) => article.id !== articleId)
            );
          } else {
            setArticles(articles.filter((article) => article.id !== articleId));
          }

          Swal.fire({
            icon: "success",
            title: "สำเร็จ",
            text: "ลบบทความเรียบร้อยแล้ว",
            confirmButtonText: "ตกลง",
          });
        }
      });
    } catch (error) {
      console.error("Error deleting article:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถลบบทความได้",
        confirmButtonText: "ตกลง",
      });
    }
  };

  // แสดง Loading
  if (loading) {
    return (
      <div className="admin-container">
        <div className="admin-content">
          <div className="admin-header">
            <h1>
              <i className="fas fa-pen-fancy"></i> จัดการบทความและแนวคิด
            </h1>
          </div>
          <div className="loading-container">
            {/* <div className="loading-spinner">กำลังโหลดข้อมูล...</div> */}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <AdminSidebar user={user} />
      <div className="admin-content">
        <div className="admin-header">
          <h1 className="admin-title">
            <i className="fas fa-pen-fancy"></i> จัดการบทความและแนวคิด
          </h1>
          <div className="admin-actions">
            <Link to="/write-article" className="btn btn-primary">
              <i className="fas fa-plus"></i> เขียนบทความใหม่
            </Link>
          </div>
        </div>

        {/* แท็บสำหรับเลือกดูข้อมูล */}
        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === "all" ? "active" : ""}`}
            onClick={() => setActiveTab("all")}
          >
            <i className="fas fa-list"></i> บทความทั้งหมด ({articles.length})
          </button>
          <button
            className={`admin-tab ${activeTab === "pending" ? "active" : ""}`}
            onClick={() => setActiveTab("pending")}
          >
            <i className="fas fa-clock"></i> รอการอนุมัติ (
            {pendingArticles.length})
          </button>
        </div>

        {/* ตารางแสดงบทความทั้งหมด */}
        {activeTab === "all" && (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: "5%" }}>#</th>
                  <th style={{ width: "15%" }}>รูปภาพ</th>
                  <th style={{ width: "30%" }}>ชื่อบทความ</th>
                  <th style={{ width: "15%" }}>หมวดหมู่</th>
                  <th style={{ width: "10%" }}>ผู้เขียน</th>
                  <th style={{ width: "10%" }}>ผู้เข้าชม</th>
                  <th style={{ width: "15%" }}>จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {articles.length > 0 ? (
                  articles.map((article, index) => (
                    <tr key={article.id}>
                      <td>{index + 1}</td>
                      <td>
                        <img
                          src={
                            article.thumbnail
                              ? `${process.env.REACT_APP_API_URL}/uploads/thumbnails/${article.thumbnail}`
                              : "/assets/default-article.jpg"
                          }
                          alt={article.title}
                          className="admin-table-image"
                        />
                      </td>
                      <td>{article.title}</td>
                      <td>{article.category_name}</td>
                      <td>{article.author_name}</td>
                      <td>{article.view_count}</td>
                      <td>
                        <div className="admin-table-actions">
                          <button
                            className="btn btn-sm btn-info"
                            onClick={() => handleViewClick(article)}
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleEditClick(article)}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteArticle(article.id)}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center">
                      ไม่พบข้อมูลบทความ
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* ตารางแสดงบทความที่รอการอนุมัติ */}
        {activeTab === "pending" && (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: "5%" }}>#</th>
                  <th style={{ width: "15%" }}>รูปภาพ</th>
                  <th style={{ width: "30%" }}>ชื่อบทความ</th>
                  <th style={{ width: "15%" }}>หมวดหมู่</th>
                  <th style={{ width: "15%" }}>ผู้เขียน</th>
                  <th style={{ width: "20%" }}>จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {pendingArticles.length > 0 ? (
                  pendingArticles.map((article, index) => (
                    <tr key={article.id}>
                      <td>{index + 1}</td>
                      <td>
                        <img
                          src={
                            article.thumbnail
                              ? `${process.env.REACT_APP_API_URL}/uploads/thumbnails/${article.thumbnail}`
                              : "/assets/default-article.jpg"
                          }
                          alt={article.title}
                          className="admin-table-image"
                        />
                      </td>
                      <td>{article.title}</td>
                      <td>{article.category_name}</td>
                      <td>{article.author_name}</td>
                      <td>
                        <div className="admin-table-actions">
                          <button
                            className="btn btn-sm btn-info"
                            onClick={() => handleViewClick(article)}
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleApproveArticle(article.id)}
                          >
                            <i className="fas fa-check"></i> อนุมัติ
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() =>
                              handleDeleteArticle(article.id, true)
                            }
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center">
                      ไม่มีบทความที่รอการอนุมัติ
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal แก้ไขบทความ */}
      {showEditModal && editingArticle && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <div className="modal-header">
              <h2>แก้ไขบทความ</h2>
              <button
                className="modal-close"
                onClick={() => setShowEditModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSaveEdit}>
                <div className="form-group">
                  <label htmlFor="articleTitle" className="form-label">
                    ชื่อบทความ
                  </label>
                  <input
                    type="text"
                    id="articleTitle"
                    className="form-control"
                    value={articleTitle}
                    onChange={(e) => setArticleTitle(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="articleCategory" className="form-label">
                    หมวดหมู่
                  </label>
                  <select
                    id="articleCategory"
                    className="form-select"
                    value={articleCategory}
                    onChange={(e) => setArticleCategory(e.target.value)}
                    required
                  >
                    <option value="">-- เลือกหมวดหมู่ --</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="articleContent" className="form-label">
                    เนื้อหาบทความ
                  </label>
                  <textarea
                    id="articleContent"
                    className="form-control"
                    value={articleContent}
                    onChange={(e) => setArticleContent(e.target.value)}
                    rows="10"
                    required
                  ></textarea>
                </div>
                <div className="form-group">
                  <label htmlFor="articleThumbnail" className="form-label">
                    รูปภาพประกอบ (ไม่จำเป็น)
                  </label>
                  <input
                    type="file"
                    id="articleThumbnail"
                    className="form-control"
                    onChange={(e) => setArticleThumbnail(e.target.files[0])}
                    accept="image/*"
                  />
                  {editingArticle.thumbnail && (
                    <div className="current-thumbnail">
                      <p>รูปภาพปัจจุบัน:</p>
                      <img
                        src={`${process.env.REACT_APP_API_URL}/uploads/thumbnails/${editingArticle.thumbnail}`}
                        alt="รูปภาพปัจจุบัน"
                        style={{ maxWidth: "100px", marginTop: "10px" }}
                      />
                    </div>
                  )}
                </div>
                <div className="form-group text-right">
                  <button
                    type="button"
                    className="btn btn-light mr-2"
                    onClick={() => setShowEditModal(false)}
                  >
                    ยกเลิก
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <i className="fas fa-save"></i> บันทึก
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal ดูรายละเอียดบทความ */}
      {showViewModal && viewingArticle && (
        <div className="modal-backdrop">
          <div className="modal-content modal-lg">
            <div className="modal-header">
              <h2>รายละเอียดบทความ</h2>
              <button
                className="modal-close"
                onClick={() => setShowViewModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="article-detail">
                <div className="article-header">
                  <h1 className="article-title">{viewingArticle.title}</h1>
                  <div className="article-meta">
                    <div className="article-author">
                      <i className="fas fa-user"></i> ผู้เขียน:{" "}
                      {viewingArticle.author_name}
                    </div>
                    <div className="article-category">
                      <i className="fas fa-folder"></i> หมวดหมู่:{" "}
                      {viewingArticle.category_name}
                    </div>
                    <div className="article-views">
                      <i className="fas fa-eye"></i> ผู้เข้าชม:{" "}
                      {viewingArticle.view_count || 0}
                    </div>
                  </div>
                </div>

                {viewingArticle.thumbnail && (
                  <div className="article-image">
                    <img
                      src={`${process.env.REACT_APP_API_URL}/uploads/thumbnails/${viewingArticle.thumbnail}`}
                      alt={viewingArticle.title}
                    />
                  </div>
                )}

                <div className="article-content">
                  {viewingArticle.content.split("\n").map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => setShowViewModal(false)}
              >
                ปิด
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminArticlesPage;

// เพิ่ม CSS สำหรับหน้า Admin ถ้ายังไม่มีไฟล์ AdminPages.css
// ไฟล์: frontend/src/pages/admin/AdminPages.css
/*
.admin-container {
  background-color: #f8f9fa;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  overflow: hidden;
}

.admin-content {
  padding: 1.5rem;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.admin-header h1 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
}

.admin-header h1 i {
  margin-right: 0.75rem;
  color: var(--primary-color);
}

.admin-tabs {
  display: flex;
  border-bottom: 1px solid var(--gray-300);
  margin-bottom: 1.5rem;
}

.tab-button {
  padding: 0.75rem 1.25rem;
  border: none;
  background: none;
  font-size: 1rem;
  font-weight: 500;
  color: var(--gray-600);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all var(--transition-fast);
}

.tab-button i {
  margin-right: 0.5rem;
}

.tab-button:hover {
  color: var(--primary-color);
}

.tab-button.active {
  color: var(--primary-color);
  border-bottom-color: var(--primary-color);
}

.admin-table-container {
  background-color: white;
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.admin-table {
  width: 100%;
  border-collapse: collapse;
}

.admin-table th,
.admin-table td {
  padding: 0.75rem 1rem;
  text-align: left;
  vertical-align: middle;
}

.admin-table thead th {
  background: var(--primary-gradient);
  color: white;
  font-weight: 500;
  border-bottom: 2px solid var(--primary-dark);
}

.admin-table tbody tr {
  border-bottom: 1px solid var(--gray-200);
}

.admin-table tbody tr:nth-child(even) {
  background-color: var(--gray-100);
}

.admin-table tbody tr:hover {
  background-color: var(--gray-200);
}

.admin-table-image {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: var(--border-radius-md);
}

.admin-table-actions {
  display: flex;
  gap: 0.5rem;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-xl);
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-content.modal-lg {
  max-width: 800px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--gray-200);
}

.modal-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: var(--gray-600);
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--gray-200);
}

.article-detail {
  padding: 1rem;
}

.article-header {
  margin-bottom: 1.5rem;
}

.article-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.article-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  color: var(--gray-600);
  font-size: 0.875rem;
}

.article-meta div {
  display: flex;
  align-items: center;
}

.article-meta i {
  margin-right: 0.5rem;
}

.article-image {
  margin-bottom: 1.5rem;
}

.article-image img {
  width: 100%;
  max-height: 300px;
  object-fit: cover;
  border-radius: var(--border-radius-md);
}

.article-content {
  line-height: 1.7;
}

.article-content p {
  margin-bottom: 1rem;
}

.current-thumbnail {
  margin-top: 0.5rem;
}

@media (max-width: 768px) {
  .admin-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .admin-table {
    display: block;
    overflow-x: auto;
  }
  
  .admin-table th,
  .admin-table td {
    white-space: nowrap;
  }
  
  .article-meta {
    flex-direction: column;
    gap: 0.5rem;
  }
}
*/
